// src/screens/MonitoringDashboard.jsx
import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiActivity } from "react-icons/fi";
import { apiFetch } from "../api";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import PullToRefreshHeader from "../components/PullToRefreshHeader";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

/* =========================
   Vocabulário oficial
========================= */

const VOCAB_STATE = {
  TOP: "Topo",
  PRE_TOP: "Possível Topo",
  VALLEY: "Vale",
  PRE_VALLEY: "Possível Vale",
  NEUTRAL: "Neutro",
};

/* =========================
   utils
========================= */

function fmtMoney(x, currency = "BRL") {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  try {
    return n.toLocaleString("pt-BR", { style: "currency", currency });
  } catch {
    return n.toFixed(2);
  }
}

function fmtPctSigned01(x01, { digits = 1 } = {}) {
  if (x01 == null) return "—";
  const n = Number(x01);
  if (!Number.isFinite(n)) return "—";
  const p = n * 100;
  const sign = p > 0 ? "+" : p < 0 ? "−" : "";
  const abs = Math.abs(p);
  // evita "-0.0"
  const val = abs < 0.05 ? 0 : abs;
  // usa pt-BR com vírgula
  const s = val.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${sign}${s}%`;
}

function normalizeSymbol(s) {
  return String(s || "")
    .trim()
    .toUpperCase()
    .replace(/^\$/, "")
    .replace(/\.SA$/, "");
}

function labelKind(text, scope) {
  if (!text) return "none";
  const s = String(text).toLowerCase();

  if (scope === "trend") {
    if (s.includes("bull")) return "buy";
    if (s.includes("bear")) return "sell";
    return "none";
  }

  // posture / outros
  if (s.includes("reação") || s.includes("reacao")) return "setup";
  if (s.includes("pullback")) return "watch";
  return "none";
}

/**
 * Agora o rating vem do back em mon.monitoring.rating (fonte única)
 * e o texto de trend vem do snapshot.regime.trendLabel
 */
function trendWithRating(mon) {
  const trend = mon?.snapshot?.regime?.trendLabel ?? null;
  if (!trend) return { text: "—", kind: "none" };
  const rate = mon?.monitoring?.rating ?? null;
  const kind = labelKind(trend, "trend");
  return rate != null ? { text: `${trend} ${rate}`, kind } : { text: trend, kind };
}

/**
 * Estado vem do snapshot.dyn (fonte única)
 */
function stateFromMonitoring(mon) {
  const d = mon?.snapshot?.dyn;
  if (!d) return { label: VOCAB_STATE.NEUTRAL, kind: "none" };

  if (d.isTop) return { label: VOCAB_STATE.TOP, kind: "sell" };
  if (d.isPreTop) return { label: VOCAB_STATE.PRE_TOP, kind: "watch" };
  if (d.isValley) return { label: VOCAB_STATE.VALLEY, kind: "buy" };
  if (d.isPreValley) return { label: VOCAB_STATE.PRE_VALLEY, kind: "buy" };

  return { label: VOCAB_STATE.NEUTRAL, kind: "none" };
}

/* =========================
   FUNDAMENTAL
========================= */

function fundKind(bucket) {
  if (bucket === "A" || bucket === "B") return "buy";
  if (bucket === "C") return "watch";
  if (bucket === "D" || bucket === "E") return "sell";
  return "none";
}

function FundamentalBadge({ fund }) {
  const b = fund?.bucket;
  if (!b) return null;
  return <LabelBadge kind={fundKind(b)}>{b}</LabelBadge>;
}

/* =========================
   component
========================= */

export default function MonitoringDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ALL | AB | ABC | NO_DE
  const [fundFilter, setFundFilter] = useState("ALL");
  // SHOW_ALL | ONLY_WITH_HISTORY | ONLY_NO_HISTORY
  const [histFilter, setHistFilter] = useState("ONLY_WITH_HISTORY");

  function openAsset(symbol) {
    const s = normalizeSymbol(symbol);
    if (s) navigate(`/assets?symbol=${encodeURIComponent(s)}`);
  }

  /* =========================
     Query (monitoringv2)
  ========================= */

  const monitoringQuery = useQuery({
    queryKey: ["monitoringv2"],
    queryFn: async () => apiFetch(`/api/portfolio/monitoringv2?onlyTriggered=false`),
    staleTime: 60_000,
  });

  // =========================
  // ✅ Pull-to-refresh (perene)
  // =========================
  const refreshAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["monitoringv2"] });
    await queryClient.refetchQueries({ queryKey: ["monitoringv2"], type: "active" });
  }, [queryClient]);

  const ptr = usePullToRefresh({
    enabled: true,
    isBlocked: false,
    startZoneY: 260,
    maxPull: 110,
    readyPull: 70,
    refreshHeight: 56,
    onRefresh: refreshAll,
  });

  const loading = monitoringQuery.isLoading;
  const err = monitoringQuery.error && String(monitoringQuery.error);
  const resp = monitoringQuery.data ?? {};

  const items = Array.isArray(resp.items) ? resp.items : [];
  const noHistoryItems = Array.isArray(resp.noHistory) ? resp.noHistory : [];

  /* =========================
     Adapter v2 → UI (com histórico)
     - Mantém snapshot/monitoring/alerts/meta do back (fonte única)
  ========================= */

  const normalized = useMemo(() => {
    return items
      .map((it) => ({
        symbol: normalizeSymbol(it.symbol),
        name: it.name ?? null,
        currency: it.currency ?? "BRL",

        snapshot: it.snapshot ?? null,
        monitoring: it.monitoring ?? null,
        alerts: it.alerts ?? null,

        fundamental: it.fundamental ?? null,
        meta: it.meta ?? { hasTechnicalHistory: true },
      }))
      .filter((it) => it.symbol);
  }, [items]);

  /* =========================
     Adapter v2 → UI (sem histórico)
     - snapshot null, mas mantém fundamental/meta do back
  ========================= */

  const normalizedNoHistory = useMemo(() => {
    return noHistoryItems
      .map((it) => ({
        symbol: normalizeSymbol(it.symbol),
        name: it.name ?? null,
        currency: it.currency ?? "BRL",

        snapshot: it.snapshot ?? null, // geralmente null
        monitoring: it.monitoring ?? null,
        alerts: it.alerts ?? null,

        fundamental: it.fundamental ?? null,
        meta: it.meta ?? { hasTechnicalHistory: false },
      }))
      .filter((it) => it.symbol);
  }, [noHistoryItems]);

  /* =========================
     Filtro FUNDAMENTAL (aplica nas duas listas)
  ========================= */

  const filtFund = (list) =>
    list.filter((it) => {
      const b = it.fundamental?.bucket ?? null;

      if (fundFilter === "AB" && !["A", "B"].includes(b)) return false;
      if (fundFilter === "ABC" && !["A", "B", "C"].includes(b)) return false;
      if (fundFilter === "NO_DE" && ["D", "E"].includes(b)) return false;

      return true;
    });

  const withHistoryAll = useMemo(() => filtFund(normalized), [normalized, fundFilter]);
  const noHistoryAll = useMemo(() => filtFund(normalizedNoHistory), [normalizedNoHistory, fundFilter]);

  /* =========================
     Hist filter controla visibilidade
  ========================= */

  const showWithHistory = histFilter !== "ONLY_NO_HISTORY";
  const showNoHistory = histFilter !== "ONLY_WITH_HISTORY";

  const withHistory = showWithHistory ? withHistoryAll : [];
  const noHistory = showNoHistory ? noHistoryAll : [];

  /* =========================
     Separação (somente com histórico)
     - triggered baseado nos flags de dyn no snapshot
  ========================= */

  const triggered = withHistory.filter((it) => {
    const d = it?.snapshot?.dyn;
    return !!(d?.isTop || d?.isPreTop || d?.isValley || d?.isPreValley);
  });

  const others = withHistory.filter((it) => !triggered.includes(it));

  /* =========================
     UI helpers
  ========================= */

  function ContextRow({ mon }) {
    const tr = trendWithRating(mon);
    const posture = mon?.snapshot?.regime?.postureLabel ?? "—";
    const postureKind = labelKind(posture, "posture");
    const state = stateFromMonitoring(mon);

    const close = mon?.snapshot?.close ?? null;

    return (
      <div style={styles.contextRow}>
        <LabelBadge kind={tr.kind}>{tr.text}</LabelBadge>
        {/* <LabelBadge kind={postureKind}>{posture}</LabelBadge> */}
        <LabelBadge kind={state.kind}>{state.label}</LabelBadge>
        <FundamentalBadge fund={mon.fundamental} />
        {mon?.meta?.hasTechnicalHistory !== true && (
          <LabelBadge kind="watch">
            SEM HIST{mon?.meta?.reason ? ` (${mon.meta.reason})` : ""}
          </LabelBadge>
        )}
        {close == null && mon?.meta?.hasTechnicalHistory === true && (
          <LabelBadge kind="watch">SNAP INCOMPLETO</LabelBadge>
        )}
      </div>
    );
  }

  function AssetCard({ it }) {
    const close = it?.snapshot?.close ?? null;
    const mm10 = it?.snapshot?.mm10 ?? null;

    // ✅ NOVO: headline = distância percentual da MM10 (snapshot-driven)
    // - se mm10/close inválidos => "—"
    // - se muito próximo de zero => "Na MM10"
    let headText = "—";
    let headKind = "none";

    if (close != null && mm10 != null) {
      const dist01 = (Number(close) - Number(mm10)) / Number(mm10);
      if (Number.isFinite(dist01)) {
        if (Math.abs(dist01) < 0.0005) {
          headText = "Na MM10";
          headKind = "none";
        } else {
          headText = `${fmtPctSigned01(dist01, { digits: 1 })} da média recente`;
          headKind = dist01 > 0 ? "buy" : "sell";
        }
      }
    }

    return (
      <div style={styles.assetBlock}>
        <div style={styles.assetTop}>
          <button onClick={() => openAsset(it.symbol)} style={styles.assetBtn} type="button">
            <div style={styles.stackCell}>
              <span style={styles.assetSymbol}>{it.symbol}</span>
              <span style={styles.subText}>{it.name}</span>
            </div>
          </button>

          {/* ✅ NOVO: 2 linhas, com contexto */}
          <div style={styles.assetPriceWrap}>
            <div
              style={{
                ...styles.assetHead,
                ...(headKind === "buy" ? styles.headBuy : null),
                ...(headKind === "sell" ? styles.headSell : null),
              }}
            >
              {headText}
            </div>
            <div style={styles.assetSubPrice}>
              {close != null ? `${fmtMoney(close, it.currency)} · último fechamento` : "—"}
            </div>
          </div>
        </div>

        <ContextRow mon={it} />
      </div>
    );
  }

  /* =========================
     render
  ========================= */

  return (
    <div
      style={styles.page}
      onTouchStart={ptr.handlers.onTouchStart}
      onTouchMove={ptr.handlers.onTouchMove}
      onTouchEnd={ptr.handlers.onTouchEnd}
      onTouchCancel={ptr.handlers.onTouchEnd}
    >
      <PullToRefreshHeader height={ptr.height} refreshing={ptr.refreshing} ready={ptr.ready} />

      <header style={styles.header}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
          <h2 style={{ margin: 0 }}>Monitoramento</h2>
          {!loading && (
            <>
              <Pill>🔥 {triggered.length}</Pill>
              <Pill>outros {others.length}</Pill>
              <Pill>sem hist {noHistory.length}</Pill>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {/* Fundamental */}
          <FilterBtn onClick={() => setFundFilter("ALL")} active={fundFilter === "ALL"}>
            ALL
          </FilterBtn>
          <FilterBtn onClick={() => setFundFilter("AB")} active={fundFilter === "AB"}>
            A–B
          </FilterBtn>
          <FilterBtn onClick={() => setFundFilter("ABC")} active={fundFilter === "ABC"}>
            A–C
          </FilterBtn>
          <FilterBtn onClick={() => setFundFilter("NO_DE")} active={fundFilter === "NO_DE"}>
            ≠ D/E
          </FilterBtn>

          <span style={{ width: 10 }} />

          {/* Histórico técnico */}
          <FilterBtn
            onClick={() => setHistFilter("ONLY_WITH_HISTORY")}
            active={histFilter === "ONLY_WITH_HISTORY"}
          >
            c/ hist
          </FilterBtn>
          <FilterBtn
            onClick={() => setHistFilter("ONLY_NO_HISTORY")}
            active={histFilter === "ONLY_NO_HISTORY"}
          >
            sem hist
          </FilterBtn>
          <FilterBtn onClick={() => setHistFilter("SHOW_ALL")} active={histFilter === "SHOW_ALL"}>
            todos
          </FilterBtn>
        </div>
      </header>

      {err && <div style={styles.error}>⚠ {err}</div>}
      {loading && <div style={{ marginTop: 14 }}>Carregando…</div>}

      {!loading && (
        <>
          {/* ✅ Estados em destaque (com histórico) */}
          {showWithHistory && (
            <section style={styles.card}>
              <div style={styles.cardTitle}>
                <FiActivity /> Estados em destaque
              </div>
              {triggered.length === 0 ? (
                <div style={{ opacity: 0.75 }}>Nenhum estado acionado.</div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {triggered.map((it) => (
                    <AssetCard key={`hot-${it.symbol}`} it={it} />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ✅ Demais ativos (com histórico) */}
          {showWithHistory && (
            <section style={styles.card}>
              <div style={styles.cardTitle}>
                <FiActivity /> Demais ativos do universo
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {others.map((it) => (
                  <AssetCard key={`all-${it.symbol}`} it={it} />
                ))}
              </div>
            </section>
          )}

          {/* ✅ Sem histórico técnico */}
          {showNoHistory && noHistory.length > 0 && (
            <section style={styles.card}>
              <div style={styles.cardTitle}>
                <FiActivity /> Ativos sem histórico técnico
              </div>
              <div style={{ opacity: 0.75, marginBottom: 10 }}>
                Estes ativos têm avaliação fundamental, mas ainda não têm histórico técnico (sem snapshots).
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {noHistory.map((it) => (
                  <AssetCard key={`nh-${it.symbol}`} it={it} />
                ))}
              </div>
            </section>
          )}

          {showNoHistory && noHistory.length === 0 && (
            <section style={styles.card}>
              <div style={styles.cardTitle}>
                <FiActivity /> Ativos sem histórico técnico
              </div>
              <div style={{ opacity: 0.75 }}>Nenhum ativo sem histórico.</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

/* =========================
   small UI
========================= */

function Pill({ children }) {
  return <span style={styles.pill}>{children}</span>;
}

function FilterBtn({ children, active, ...props }) {
  return (
    <button
      {...props}
      style={{
        ...styles.filterBtn,
        ...(active ? styles.filterBtnActive : null),
      }}
      type="button"
    >
      {children}
    </button>
  );
}

function LabelBadge({ kind = "none", children }) {
  const s = {
    ...styles.labelBadge,
    ...(kind === "buy" ? styles.badgeBuy : null),
    ...(kind === "sell" ? styles.badgeSell : null),
    ...(kind === "watch" ? styles.badgeWatch : null),
    ...(kind === "setup" ? styles.badgeSetup : null),
  };
  return <span style={s}>{children}</span>;
}

/* =========================
   styles
========================= */

const styles = {
  page: { padding: 12, maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 },

  pill: {
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.06)",
  },

  filterBtn: {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.04)",
    cursor: "pointer",
    color: "rgba(255,255,255,0.75)", // 👈 inativo
  },

  filterBtnActive: {
    background: "rgba(255,255,255,0.12)",
    fontWeight: 800,
    color: "rgba(255,255,255,1)", // 👈 ativo
  },

  error: {
    marginTop: 12,
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(255,100,100,0.25)",
    background: "rgba(255,100,100,0.06)",
  },

  card: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.03)",
  },

  cardTitle: {
    fontWeight: 900,
    marginBottom: 10,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },

  assetBlock: {
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.02)",
  },

  assetTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "flex-start",
  },

  contextRow: {
    marginTop: 10,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  labelBadge: {
    padding: "3px 9px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.06)",
    fontWeight: 800,
    color: "rgba(255,255,255,0.9)",
  },

  badgeBuy: { border: "1px solid rgba(80,200,120,0.35)", background: "rgba(80,200,120,0.10)" },
  badgeSell: { border: "1px solid rgba(255,90,90,0.35)", background: "rgba(255,90,90,0.10)" },
  badgeWatch: { border: "1px solid rgba(255,200,80,0.35)", background: "rgba(255,200,80,0.10)" },
  badgeSetup: { border: "1px solid rgba(120,170,255,0.35)", background: "rgba(120,170,255,0.10)" },

  assetBtn: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "inherit",
    textAlign: "left",
  },

  stackCell: { display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.15 },
  assetSymbol: { fontWeight: 900, lineHeight: "18px" },
  subText: { fontSize: 12, opacity: 0.7, lineHeight: "16px" },

  // ✅ NOVO bloco de preço (2 linhas)
  assetPriceWrap: { textAlign: "right", paddingTop: 0, minWidth: 180 },
  assetHead: { fontSize: 12, fontWeight: 700, lineHeight: "18px" },
  assetSubPrice: { fontSize: 12, opacity: 0.75, lineHeight: "16px", marginTop: 2 },

  headBuy: { color: "rgba(150,255,190,0.95)" },
  headSell: { color: "rgba(255,150,150,0.95)" },
};
